// Priority-based upload queue manager
import SparkMD5 from 'spark-md5';

export class UploadQueue {
  constructor() {
    this.queue = []; // { photoId, size, blob, priority, status, attempts }
    this.maxConcurrent = 3;
    this.listeners = new Map(); // photoId -> { progress: [], complete: [], error: [] }
  }
  
  /**
   * Add a single upload to the queue
   */
  addUpload(photoId, size, blob, priority = 0, deferProcessing = false) {
    this.queue.push({
      photoId,
      size,
      blob,
      priority,
      status: 'queued',
      attempts: 0
    });
    
    if (!deferProcessing) {
      this.sortQueue();
      this.processQueue();
    }
  }
  
  /**
   * Add all size uploads for a photo
   * Set deferProcessing=true to batch multiple photos before starting uploads
   */
  addPhotoUploads(photoId, sizeBlobs, priority = 0, deferProcessing = false) {
    // Add all sizes for a photo in priority order: 256, 512, 1024, 768, original
    const sizeOrder = [256, 512, 1024, 768, 'original'];
    
    sizeOrder.forEach(size => {
      if (sizeBlobs[size]) {
        this.addUpload(photoId, size, sizeBlobs[size], priority, true); // Always defer within batch
      }
    });
    
    // Only sort and process if not deferring
    if (!deferProcessing) {
      this.sortQueue();
      this.processQueue();
    }
  }
  
  /**
   * Start processing the queue (call after batching uploads)
   */
  startProcessing() {
    this.sortQueue();
    this.processQueue();
  }
  
  /**
   * Prioritize all remaining uploads for a photo
   */
  prioritizePhoto(photoId) {
    // Move all remaining uploads for this photo to front
    this.queue.forEach(item => {
      if (item.photoId === photoId && item.status === 'queued') {
        item.priority = 10;
      }
    });
    
    this.sortQueue();
    this.processQueue();
  }
  
  /**
   * Sort queue by priority and size order
   */
  sortQueue() {
    this.queue.sort((a, b) => {
      // Non-queued items stay in place
      if (a.status !== 'queued' && b.status !== 'queued') return 0;
      if (a.status !== 'queued') return 1;
      if (b.status !== 'queued') return -1;
      
      // Higher priority first
      if (a.priority !== b.priority) return b.priority - a.priority;
      
      // Then by size (256 before 512, etc.)
      const sizeOrder = { 256: 0, 512: 1, 1024: 2, 768: 3, 'original': 4 };
      return sizeOrder[a.size] - sizeOrder[b.size];
    });
  }
  
  /**
   * Process queue - start uploads up to maxConcurrent
   */
  async processQueue() {
    const queued = this.queue.filter(item => item.status === 'queued');
    const active = this.queue.filter(item => item.status === 'uploading');
    
    const available = this.maxConcurrent - active.length;
    const toProcess = queued.slice(0, available);
    
    toProcess.forEach(item => this.uploadItem(item));
  }
  
  /**
   * Upload a single item
   */
  async uploadItem(item) {
    item.status = 'uploading';
    
    try {
      // 1. Get presigned URL from backend
      const directUploadData = await this.getDirectUploadUrl(item);
      
      // 2. Upload blob to B2
      await this.uploadToB2(directUploadData, item.blob, (progress) => {
        this.notifyProgress(item.photoId, item.size, progress);
      });
      
      // 3. Attach variant to photo and get updated photo data
      const updatedPhoto = await this.attachVariant(item.photoId, item.size, directUploadData.signed_id);
      
      item.status = 'completed';
      this.notifyComplete(item.photoId, item.size, updatedPhoto);
      
    } catch (error) {
      console.error('Upload failed:', error);
      item.attempts++;
      
      if (item.attempts < 3) {
        item.status = 'queued'; // Retry
      } else {
        item.status = 'failed';
        this.notifyError(item.photoId, item.size, error);
      }
    }
    
    // Continue processing queue
    this.processQueue();
  }
  
  /**
   * Get presigned upload URL from backend
   */
  async getDirectUploadUrl(item) {
    const checksum = await this.computeChecksum(item.blob);
    
    console.log(`[UploadQueue] Getting upload URL for photo ${item.photoId} size ${item.size}`);
    console.log(`[UploadQueue] Blob size: ${item.blob.size}, checksum: ${checksum}`);
    
    const response = await fetch('/api/v1/direct_uploads', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        blob: {
          filename: `${item.photoId}_${item.size}.jpg`,
          byte_size: item.blob.size,
          checksum: checksum,
          content_type: item.blob.type || 'image/jpeg',
          metadata: {
            photo_id: item.photoId.toString(),
            variant: item.size.toString()
          }
        }
      })
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error(`[UploadQueue] Failed to get upload URL:`, errorText);
      throw new Error(`Failed to get upload URL: ${response.status} - ${errorText}`);
    }
    
    const result = await response.json();
    console.log(`[UploadQueue] Got upload URL, headers:`, result.direct_upload.headers);
    return result;
  }
  
  /**
   * Upload blob to B2 using presigned URL
   */
  async uploadToB2(directUploadData, blob, onProgress) {
    const { url, headers } = directUploadData.direct_upload;
    
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      
      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable) {
          onProgress(e.loaded / e.total);
        }
      });
      
      xhr.addEventListener('load', () => {
        if (xhr.status === 200 || xhr.status === 204) {
          resolve();
        } else {
          // Log the response body for debugging
          console.error('Upload failed with status:', xhr.status);
          console.error('Response:', xhr.responseText);
          console.error('Headers sent:', headers);
          reject(new Error(`Upload failed: ${xhr.status} - ${xhr.responseText}`));
        }
      });
      
      xhr.addEventListener('error', () => reject(new Error('Network error')));
      xhr.addEventListener('abort', () => reject(new Error('Upload aborted')));
      
      xhr.open('PUT', url);
      
      // Set headers from Active Storage
      Object.entries(headers).forEach(([key, value]) => {
        xhr.setRequestHeader(key, value);
      });
      
      xhr.send(blob);
    });
  }
  
  /**
   * Attach uploaded variant to photo record
   */
  async attachVariant(photoId, size, signedBlobId) {
    const response = await fetch(`/api/v1/photos/${photoId}/attach_variant`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        variant: size.toString(),
        signed_blob_id: signedBlobId
      })
    });
    
    if (!response.ok) {
      throw new Error(`Failed to attach variant: ${response.status}`);
    }
    
    return await response.json();
  }
  
  /**
   * Compute SHA-256 checksum for a blob (required by Active Storage)
   */
  async computeChecksum(blob) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const spark = new SparkMD5.ArrayBuffer();
        spark.append(e.target.result);
        const hexHash = spark.end(false); // Get hex hash
        
        // Convert hex to binary bytes
        const bytes = new Uint8Array(hexHash.match(/.{2}/g).map(byte => parseInt(byte, 16)));
        
        // Convert bytes to base64
        const base64 = btoa(String.fromCharCode(...bytes));
        resolve(base64);
      };
      reader.onerror = reject;
      reader.readAsArrayBuffer(blob);
    });
  }
  
  /**
   * Register a progress listener for a photo
   */
  onProgress(photoId, callback) {
    if (!this.listeners.has(photoId)) {
      this.listeners.set(photoId, { progress: [], complete: [], error: [] });
    }
    this.listeners.get(photoId).progress.push(callback);
  }
  
  /**
   * Register a completion listener for a photo
   */
  onComplete(photoId, callback) {
    if (!this.listeners.has(photoId)) {
      this.listeners.set(photoId, { progress: [], complete: [], error: [] });
    }
    this.listeners.get(photoId).complete.push(callback);
  }
  
  /**
   * Register an error listener for a photo
   */
  onError(photoId, callback) {
    if (!this.listeners.has(photoId)) {
      this.listeners.set(photoId, { progress: [], complete: [], error: [] });
    }
    this.listeners.get(photoId).error.push(callback);
  }
  
  /**
   * Notify progress listeners
   */
  notifyProgress(photoId, size, progress) {
    const listeners = this.listeners.get(photoId)?.progress || [];
    listeners.forEach(callback => callback(size, progress));
  }
  
  /**
   * Notify completion listeners
   */
  notifyComplete(photoId, size, updatedPhoto) {
    const listeners = this.listeners.get(photoId)?.complete || [];
    listeners.forEach(callback => callback(size, updatedPhoto));
  }
  
  /**
   * Notify error listeners
   */
  notifyError(photoId, size, error) {
    const listeners = this.listeners.get(photoId)?.error || [];
    listeners.forEach(callback => callback(size, error));
  }
  
  /**
   * Get upload state for a specific photo
   */
  getPhotoUploadState(photoId) {
    const items = this.queue.filter(item => item.photoId === photoId);
    return {
      total: items.length,
      completed: items.filter(i => i.status === 'completed').length,
      uploading: items.filter(i => i.status === 'uploading').length,
      failed: items.filter(i => i.status === 'failed').length,
      queued: items.filter(i => i.status === 'queued').length
    };
  }
  
  /**
   * Check if a photo has pending uploads
   */
  hasPendingUploads(photoId) {
    const state = this.getPhotoUploadState(photoId);
    return state.uploading > 0 || state.queued > 0;
  }
  
  /**
   * Get serializable queue state (for persistence)
   */
  getSerializableState() {
    // Don't serialize blobs, just metadata
    return this.queue.map(item => ({
      photoId: item.photoId,
      size: item.size,
      priority: item.priority,
      status: item.status,
      attempts: item.attempts
    }));
  }
}

